import { take, put, select, all, race, delay, fork, cancel } from "redux-saga/effects";
import { Task } from "redux-saga";
import { Action } from "redux";
import { isEqual } from "lodash";

import client from "../../../api/client/SdsWebApiClient";

import { ConfigEncoding, ImageAndMaskResponse, ImageSeries } from "../../../api/generated";

import { CONFIGURATION_LOADED, SET_CONFIGURATION, SET_IMAGE_URL } from "../../actions/configurationActions";
import { SET_IS_LOADING } from "../../actions/sessionActions";

import {
    getComposedUrl,
    getAppConfig,
    getActiveFrame,
    getActiveImageSeriesId,
    getConfigurationString,
    getPreviewConfig,
    getDealerAccount,
    getCurrentImageSeries,
} from "../selectors";
import { RESOLUTION_CHANGED, SET_ACTIVE_SERIES_AND_FRAME, SET_ACTIVE_VIEW_INDEX } from "../../actions/viewActions";
import Constants from "../../../constants";
import { PreviewConfig } from "../../../models/previewConfig";

type ImageParameters = {
    dealer: string;
    config: string;
    seriesId: string;
    frame: number;
    width: number;
    height: number;
    bgColorHex: string;
};

function* fetchImage(params: ImageParameters): any {
    const imageAndMaskPaths: ImageAndMaskResponse = yield client.getConfigImageAndMaskGet(
        params.dealer,
        params.config,
        ConfigEncoding.Default,
        params.seriesId,
        params.frame,
        params.width,
        params.height,
        0.5,
        0.5,
        0,
        0,
        "",
        "",
        "",
        "",
        "jpg",
        false,
        params.bgColorHex,
        false,
        false
    );

    const composedUrl: string = yield select(getComposedUrl);
    const imageUrl = composedUrl + imageAndMaskPaths.imagePath;

    yield all([
        put({
            type: SET_IMAGE_URL,
            imageUrl,
        }),
        put({ type: SET_IS_LOADING, isLoading: false }),
    ]);
}

export function* getConfigurationImageSaga(): any {
    // Debounce on RESOLUTION_CHANGED, but request new image immediately on the other actions.
    const actions = [
        SET_CONFIGURATION,
        CONFIGURATION_LOADED,
        SET_ACTIVE_VIEW_INDEX,
        SET_ACTIVE_SERIES_AND_FRAME,
        RESOLUTION_CHANGED,
    ];
    let lastImageParams: ImageParameters | null = null;
    let currentRequest: Task | null = null;
    while (true) {
        const action: Action<any> = yield take(actions);
        if (action.type === RESOLUTION_CHANGED) {
            while (true) {
                const { debounced, latestAction } = yield race({
                    debounced: delay(500),
                    latestAction: take(actions),
                });

                if (debounced || latestAction.type !== RESOLUTION_CHANGED) {
                    break;
                }
            }
        }

        const seriesId: string | null = yield select(getActiveImageSeriesId);
        const series: ImageSeries | null = yield select(getCurrentImageSeries);
        if (!seriesId || !series) continue;

        const dealer: string = yield select(getDealerAccount);
        const frame: number = yield select(getActiveFrame);
        const appConfig = yield select(getAppConfig);
        const previewConfig: PreviewConfig = yield select(getPreviewConfig);
        const configString = yield select(getConfigurationString);
        const deviceWidth = window.innerWidth;
        const deviceHeight = window.innerHeight;
        const landscape = deviceWidth > deviceHeight;
        let height;
        let width;
        if (deviceWidth <= 896) {
            if (landscape) {
                // Show full size image in landscape mode on mobile (not tablet).
                height = 0;
                width = deviceWidth;
            } else {
                // Adjust size for portrait mobile mode.
                width = 0;
                height = Math.ceil((deviceWidth * Constants.mobileImageHeightVw) / 100);
            }
        } else {
            // Adjust size for desktop and landscape tablet.
            width = 0;
            height = Math.ceil((deviceHeight * Constants.desktopImageHeightPercent) / 100);
        }

        if (window.devicePixelRatio) {
            width = Math.round(width * window.devicePixelRatio);
            height = Math.round(height * window.devicePixelRatio);
        }

        width = Math.min(width, series.imageWidth);
        height = Math.min(height, series.imageHeight);

        const bgColorMatch = appConfig.style.background.color.match(/^#?([0-9a-f]+)$/i);
        let bgColorHex = "#ffffffff";
        if (bgColorMatch) {
            const col = bgColorMatch[1];
            if (col.length === 3) {
                bgColorHex = `#ff${col[0]}${col[0]}${col[1]}${col[1]}${col[2]}${col[2]}`;
            } else {
                bgColorHex = `#${col.padStart(8, "f")}`;
            }
        }

        const imageParams: ImageParameters = {
            dealer,
            config: previewConfig?.configurationString || configString,
            seriesId,
            frame,
            width,
            height,
            bgColorHex,
        };

        // Cache the image input parameters and skip request when they are still the same.
        if (lastImageParams && isEqual(imageParams, lastImageParams)) {
            continue;
        }

        if (currentRequest && currentRequest.isRunning()) {
            yield cancel(currentRequest);
        }

        lastImageParams = imageParams;
        currentRequest = yield fork(fetchImage, imageParams);
    }
}
